/**
 * client/src/help/pageLabels.ts
 *
 * The names users know pages by (ST-00200): the sidebar's labels (buildMenuItems — the single
 * inventory of menu entries), with the route-label table behind tab titles as the fallback.
 * Also tells Help which menu entries leave the single-page app (the support platform at
 * /support is served by nginx, not by a React route).
 */
import { buildMenuItems, type SidebarItem } from '../components/layout/EnhancedSidebar';
import { getLabelForRoute } from '../hooks/useLastVisitedRoute';
import { normalizePath } from './routes';

let menu: SidebarItem[] | null = null;
const menuItems = (): SidebarItem[] => (menu ??= buildMenuItems(0));

/** The sidebar entry for `path` (exact), else the one whose path is its longest prefix. */
export function menuItemFor(path: string): SidebarItem | null {
  const p = normalizePath(path);
  let best: SidebarItem | null = null;
  for (const item of menuItems()) {
    if (item.path === p) return item;
    if (item.path !== '/' && p.startsWith(`${item.path}/`) && (!best || item.path.length > best.path.length)) {
      best = item;
    }
  }
  return best;
}

/** "Plan & Dispatch" for /lexistar/plan; the tab-title label for pages outside the menu. */
export function pageLabel(path: string): string {
  const p = normalizePath(path);
  if (p === '/') return 'Operations Overview';
  const exact = menuItems().find((i) => i.path === p);
  if (exact) return exact.label;
  const label = getLabelForRoute(p);
  return label || menuItemFor(p)?.label || p;
}

/** Full-page URL for a menu entry that is not a React route (e.g. /support → /support/), else null. */
export function externalHref(path: string): string | null {
  const p = normalizePath(path);
  const item = menuItems().find((i) => i.external && i.path === p);
  return item ? (item.href ?? item.path) : null;
}
